/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useCallback, useMemo } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from 'react-apollo';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getApolloCache } from '@confluence/graphql/entry-points/createClient.ts';
import { useAccessStatus } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { expVal } from '@confluence/feature-experiments';

import { isExpectedError } from '../../utils/isExpectedError.ts';
import {
	OnboardingKeysCombined,
	type OnboardingKeysCombinedType,
} from '../../constants/onboarding-keys-combined.ts';
import { filterOnboardingState } from '../../utils/filterOnboardingState.ts';

import { getOnboardingStateGQL } from './OnboardingStatus.graphql.ts';
import type {
	GetOnboardingState,
	GetOnboardingStateVariables,
} from './__types__/getOnboardingStateGQL.ts';
import type {
	OnboardingStateInput,
	onboardingStateSetOnboardingStateMutation as onboardingStateSetOnboardingStateMutation$data,
	onboardingStateSetOnboardingStateMutationVariables as onboardingStateSetOnboardingStateMutation$variables,
} from './__types__/onboardingStateSetOnboardingStateMutation.ts';
import type {
	onboardingStateGetOnboardingStateQuery as onboardingStateGetOnboardingStateQuery$data,
	onboardingStateGetOnboardingStateQueryVariables as onboardingStateGetOnboardingStateQuery$variables,
} from './__types__/onboardingStateGetOnboardingStateQuery.ts';

// Relay generated types are immutable (readonly) whereas Apollo expects mutable types
type OnboardingStateData = {
	onboardingState?: readonly { key: string; value: string | null | undefined }[] | null;
};

export const deserializeState = (
	data: onboardingStateGetOnboardingStateQuery$data | OnboardingStateData | null | undefined,
) => {
	const state = {} as any;
	// new graphQL call returns onboardingState array
	const { onboardingState } = data || {};

	// parse data and populate state accordingly
	if (onboardingState !== undefined && onboardingState !== null) {
		onboardingState.forEach(({ key, value }) => {
			// key comes back as "{prefix}:{property}" format
			// split by ':' to get parsed {property}
			// some keys don't have a prefix, which is why we have the ||
			const parsedKey = key.split(':')[1] || key;
			if (!value) {
				return;
			}
			if (/(true|false)/g.test(value)) {
				state[parsedKey] = value === 'true';
			} else {
				state[parsedKey] = value;
			}
		});
	}
	return state;
};

export const useSetOnboardingState = () => {
	const [setState, { error, called }] = useMutation<
		onboardingStateSetOnboardingStateMutation$data,
		onboardingStateSetOnboardingStateMutation$variables
	>(
		gql`
			mutation onboardingStateSetOnboardingStateMutation($states: [OnboardingStateInput!]!) {
				setOnboardingState(states: $states) {
					key
					value
				}
			}
		`,
		{
			update(cache, { data }) {
				const states = data!.setOnboardingState;
				states.forEach(({ key, value }) => {
					// writeFragment can only handle a single data item at a time
					cache.writeFragment({
						id: `OnboardingState:${key}`,
						fragment: gql`
							fragment myKey on OnboardingState {
								key
								value
							}
						`,
						data: {
							key,
							value,
							__typename: 'OnboardingState',
						},
					});
				});
			},
			onError: (error) => {
				if (isExpectedError(error)) {
					markErrorAsHandled(error);
				}
			},
		},
	);

	type SetOnboardingStateArgs = OnboardingStateInput | OnboardingStateInput[];

	const setOnboardingState = useCallback(
		(newOnboardingState: SetOnboardingStateArgs) => {
			let states: OnboardingStateInput[];
			if (Array.isArray(newOnboardingState)) {
				// supports batch updates
				states = newOnboardingState;
			} else {
				// backwards compability for single key-value pair
				states = [newOnboardingState];
			}

			void setState({
				variables: {
					states,
				},
				optimisticResponse: {
					setOnboardingState: states.map(({ key, value }) => ({
						key,
						value,
						__typename: 'OnboardingState',
					})),
				},
			});
		},
		[setState],
	);

	return { setOnboardingState, error, called };
};

// safeguards against empty arrays or strings
const hasValidKeysToFetch = (
	key: OnboardingKeysCombinedType | OnboardingKeysCombinedType[],
): boolean => {
	if (Array.isArray(key)) return key.length > 0;
	if (typeof key === 'string') return key.length > 0;
	return false;
};

/**
 * Reads onboarding state from Apollo cache and returns filtered results.
 * When the centralized experiment is enabled, reads with all keys to hit cache,
 * then filters to only the requested keys.
 *
 * @param requestedKeys - The keys the consumer wants to fetch
 * @returns The filtered onboarding state data containing only requested keys
 */
export const getOnboardingStateFromCache = (requestedKeys: string[]): GetOnboardingState | null => {
	const variables = expVal('confluence_centralized_onboarding_state', 'isEnabled', false)
		? { key: OnboardingKeysCombined as unknown as string[] }
		: { key: requestedKeys };

	const data = getApolloCache().readQuery<GetOnboardingState, GetOnboardingStateVariables>({
		query: getOnboardingStateGQL,
		variables,
	});

	if (!expVal('confluence_centralized_onboarding_state', 'isEnabled', false) || !data) {
		return data;
	}

	return filterOnboardingState(data, requestedKeys) as GetOnboardingState;
};

// TODO: Migrate to fetch from userPreferences - this is considered deprecated
export const useGetOnboardingState = (
	key: OnboardingKeysCombinedType | OnboardingKeysCombinedType[],
	skip = false,
) => {
	const { isLicensed, isGuest } = useAccessStatus();
	const isAllowed = isLicensed || isGuest;

	const hasKeysToFetch = hasValidKeysToFetch(key);
	const skipQuery = skip || !isAllowed || !hasKeysToFetch;

	// When enabled, always fetch ALL keys to ensure cache hits across different consumers.
	// This allows the preloaded data to be reused regardless of which subset of keys is requested.
	const queryVariables = expVal('confluence_centralized_onboarding_state', 'isEnabled', false)
		? { key: OnboardingKeysCombined as OnboardingKeysCombinedType[] }
		: { key };

	const {
		data: fullData,
		error,
		loading,
		refetch,
	} = useQuery<
		onboardingStateGetOnboardingStateQuery$data,
		onboardingStateGetOnboardingStateQuery$variables
	>(
		expVal('confluence_consolidate_onboarding_state_queries', 'isEnabled', false)
			? // eslint-disable-next-line graphql-relay-compat/consistent-graphql-operation-naming -- Preloaded GraphQL Operation
				gql`
					query GetOnboardingState($key: [String]) {
						onboardingState(key: $key) {
							key
							value
						}
					}
				`
			: gql`
					query onboardingStateGetOnboardingStateQuery($key: [String]) {
						onboardingState(key: $key) {
							key
							value
						}
					}
				`,
		{
			variables: queryVariables,
			skip: skipQuery,
		},
	);

	// Filter the response to only include the keys that the consumer requested.
	// When the centralized experiment is enabled, we fetch all keys but only return
	// the ones the consumer requested.
	const data = useMemo(() => {
		if (!expVal('confluence_centralized_onboarding_state', 'isEnabled', false) || !fullData) {
			return fullData;
		}
		return filterOnboardingState(fullData, key);
	}, [fullData, key]);

	return {
		data,
		error,
		loading,
		refetch,
	};
};
